/**
 * Simular: o que a automação faria com um evento de exemplo.
 *
 * O serviço roda a versão salva (a que está no ar, ou a em teste) e devolve o
 * caminho, as ações que pediria e os prazos que agendaria. Nada é enviado,
 * agendado nem gravado. A simulação é da versão salva: com alteração não
 * salva, o botão diz para salvar antes.
 *
 * O desenho segue o protótipo v2: cada passo com o ícone do bloco na cor do
 * grupo e a saída na cor do caminho, entrando um depois do outro; e "Ver o
 * caminho no mapa" anda pelo mapa com o mesmo caminho. O protótipo simulava
 * uma conversa inventada; aqui só aparece o que o serviço devolveu.
 *
 * O resultado guarda a versão que o gerou, pelo número: trocar o seletor ou
 * salvar uma versão nova depois de simular não faz o caminho de uma versão
 * andar no mapa da outra.
 */

import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AutomacaoDaApi, BlocosDoEditor, ClienteDaApi } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  Botao,
  Campo,
  CampoSelecao,
  Dialogo,
  Icone,
  Segmentado,
  Selo,
  type TomSelo,
} from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { COR_DO_TOM, grupoDoTipo, iconeDoTipo, tipoDoPasso, tomDaSaida } from '../lib/aparencia';
import { descreverNo, rotuloDaSaida } from '../lib/mapa';
import { resumoDoComeco } from '../lib/comeco';

const ID_DO_FORMULARIO = 'simular-automacao';

export type ResultadoDaSimulacao = Awaited<ReturnType<ClienteDaApi['simularAutomacao']>>;
export type VersaoSimulada = 'ativa' | 'pendente';
/** Uma simulação feita: qual versão, o número dela na hora, o evento de exemplo e a resposta. */
export type Simulacao = {
  versao: VersaoSimulada;
  numero: number;
  /** Por qual começo (#1183): 0 é o gatilho; 1 em diante, os outros. */
  comeco?: number;
  contexto: Record<string, string>;
  dados: ResultadoDaSimulacao;
};

export const RESULTADO_DA_SIMULACAO: Record<string, { rotulo: string; tom: TomSelo }> = {
  concluida: { rotulo: 'Terminaria', tom: 'ok' },
  ignorada: { rotulo: 'Não rodaria', tom: 'neutro' },
  falhou: { rotulo: 'Falharia', tom: 'erro' },
  esperando: { rotulo: 'Pararia esperando', tom: 'info' },
};

const QUANDO = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function Simular({
  automacao: a,
  blocos,
  versaoDoMapa,
  anterior,
  aoFechar,
  aoSimular,
  aoVerNoMapa,
}: {
  automacao: AutomacaoDaApi;
  blocos: BlocosDoEditor | undefined;
  /** A versão que o mapa aberto mostra: o caminho só anda nele se for a simulada. */
  versaoDoMapa: number;
  /** A última simulação, para o resultado voltar depois de ver o caminho no mapa. */
  anterior?: Simulacao | null;
  aoFechar: () => void;
  /** Cada simulação que deu certo, para ela voltar quando o painel abrir de novo. */
  aoSimular?: (simulacao: Simulacao) => void;
  /** Anda pelo mapa com o caminho simulado. Só quando o mapa aberto é a versão simulada. */
  aoVerNoMapa?: (simulacao: Simulacao) => void;
}): ReactNode {
  const api = useApi();
  const [escolhida, definirVersao] = useState<VersaoSimulada>(
    anterior?.versao ?? (a.pendente ? 'pendente' : 'ativa'),
  );
  /*
   * Sem versão em teste, só há a do ar: a escolha que veio da simulação de
   * antes pode ser de uma em teste que já foi ativada ou descartada.
   */
  const versao: VersaoSimulada = a.pendente ? escolhida : 'ativa';
  const [contexto, definirContexto] = useState<Record<string, string>>(anterior?.contexto ?? {});

  const fonteDe = (v: VersaoSimulada): AutomacaoDaApi | NonNullable<AutomacaoDaApi['pendente']> =>
    v === 'pendente' && a.pendente ? a.pendente : a;
  const fonte = fonteDe(versao);
  const [comecoEscolhido, definirComeco] = useState(anterior?.comeco ?? 0);
  // O começo que a outra versão não tem volta para o principal.
  const comeco = comecoEscolhido <= (fonte.outros_comecos ?? []).length ? comecoEscolhido : 0;
  const eventoDoComeco =
    comeco > 0
      ? fonte.outros_comecos[comeco - 1]?.evento
      : fonte.gatilho.tipo === 'evento'
        ? fonte.gatilho.evento
        : undefined;
  const evento = eventoDoComeco
    ? blocos?.eventos.find((e) => e.evento === eventoDoComeco)
    : undefined;
  const campos = evento?.contexto ?? [];

  const simular = useMutation({
    mutationFn: (pedido: Omit<Simulacao, 'dados'>) =>
      api!.simularAutomacao({
        caminho: { id: a.id },
        corpo: {
          versao: pedido.versao,
          // O começo principal é o padrão do contrato: só vai quando é outro.
          ...(pedido.comeco ? { comeco: pedido.comeco } : {}),
          contexto: Object.fromEntries(
            Object.entries(pedido.contexto).filter(([, valor]) => valor.trim() !== ''),
          ),
        },
      }),
    onSuccess: (dados, pedido) => aoSimular?.({ ...pedido, dados }),
  });

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    if (!simular.isPending) {
      simular.mutate({ versao, numero: fonteDe(versao).versao, comeco, contexto });
    }
  };

  /*
   * O resultado na tela é o da última simulação, com a versão que o gerou. O
   * anterior só aparece antes de simular aqui: enquanto a nova roda, ou depois
   * de ela falhar, ele passaria pela resposta dela.
   */
  const ultima: Simulacao | null =
    simular.data && simular.variables
      ? { ...simular.variables, dados: simular.data }
      : simular.isIdle
        ? (anterior ?? null)
        : null;
  const r = ultima?.dados;
  const simulada = ultima ? fonteDe(ultima.versao) : fonte;
  const acoesDoCatalogo = blocos?.acoes.map((x) => x.acao) ?? [];

  /** O bloco do passo no mapa simulado, também dentro de um "para cada". */
  const blocoDoPasso = (no: string | null) => {
    if (!no || !simulada.grafo) return undefined;
    for (const n of simulada.grafo.nos) {
      if (n.id === no) return n;
      if (n.tipo === 'para_cada') {
        const dentro = n.corpo.nos.find((c) => c.id === no);
        if (dentro) return dentro;
      }
    }
    return undefined;
  };
  const tituloDaAcao = (acao: string): string =>
    blocos?.acoes.find((x) => x.acao === acao)?.titulo ?? acao;
  const tituloDoPasso = (no: string | null, tipo: string): string => {
    const doGrafo = blocoDoPasso(no);
    if (doGrafo) return descreverNo(doGrafo, blocos).titulo;
    if (no === null) return 'Começo';
    if (acoesDoCatalogo.includes(tipo)) return tituloDaAcao(tipo);
    return blocos?.nos.find((n) => n.tipo === tipo)?.titulo ?? 'Um bloco';
  };

  // Pelo número da versão na hora da simulação, e não pelo de agora.
  const daVersaoDoMapa = ultima !== null && ultima.numero === versaoDoMapa;
  const daVersaoEscolhida = ultima !== null && ultima.numero === fonteDe(versao).versao;
  return (
    <Dialogo
      titulo="Simular"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Fechar
          </Botao>
          <Botao
            type="submit"
            form={ID_DO_FORMULARIO}
            variante="primario"
            desabilitado={simular.isPending || api === null}
            motivo={simular.isPending ? 'Simulando…' : 'Sem sessão: entre de novo.'}
          >
            {simular.isPending ? 'Simulando…' : r ? 'Simular de novo' : 'Simular'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} className="editor-do-mapa__simular" onSubmit={enviar} noValidate>
        <p className="editor-do-mapa__simular-aviso">
          <Icone nome="check" />
          <span>
            Roda a automação com um evento de exemplo. Nada é enviado, agendado nem gravado.
          </span>
        </p>

        {a.pendente && (
          <Segmentado
            rotulo="Qual versão"
            como="grupo"
            opcoes={[
              { valor: 'pendente', rotulo: `A em teste (v${a.pendente.versao})` },
              { valor: 'ativa', rotulo: `A no ar (v${a.versao})` },
            ]}
            escolhida={versao}
            aoEscolher={definirVersao}
          />
        )}

        {(fonte.outros_comecos ?? []).length > 0 && (
          <CampoSelecao
            rotulo="Por qual começo"
            tamanho="miudo"
            value={String(comeco)}
            onChange={(e) => definirComeco(Number(e.target.value))}
          >
            <option value="0">{resumoDoComeco(fonte.gatilho, blocos)}</option>
            {fonte.outros_comecos.map((o, i) => (
              <option key={i} value={String(i + 1)}>
                {resumoDoComeco({ tipo: 'evento', evento: o.evento }, blocos)}
              </option>
            ))}
          </CampoSelecao>
        )}

        {campos.length > 0 ? (
          <fieldset className="editor-do-mapa__simular-grupo">
            <legend>O evento de exemplo{evento ? `: ${evento.titulo}` : ''}</legend>
            <p className="editor-do-mapa__painel-ajuda">
              Use ids que existem nesta organização: a simulação lê o CRM de verdade.
            </p>
            {campos.map((c) => (
              <Campo
                key={c}
                rotulo={c}
                spellCheck={false}
                value={contexto[c] ?? ''}
                onChange={(e) => definirContexto({ ...contexto, [c]: e.target.value })}
              />
            ))}
          </fieldset>
        ) : (
          <p className="editor-do-mapa__painel-ajuda">
            Esta automação não pede dados de exemplo: ela roda como se o horário dela chegasse
            agora.
          </p>
        )}

        {simular.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(simular.error, {
              generica: 'Não foi possível simular agora. Tente de novo.',
              porCodigo: {
                ENGINE_INDISPONIVEL:
                  'O serviço das automações não respondeu. Tente de novo daqui a pouco.',
              },
            })}
          </Faixa>
        )}

        <div role="status" aria-live="polite">
          {r && ultima && (
            <section className="editor-do-mapa__resultado" aria-label="O que aconteceria">
              <p className="editor-do-mapa__resultado-topo" data-resultado={r.resultado}>
                <Selo tom={RESULTADO_DA_SIMULACAO[r.resultado]?.tom ?? 'neutro'}>
                  {RESULTADO_DA_SIMULACAO[r.resultado]?.rotulo ?? r.resultado}
                </Selo>{' '}
                {r.motivo && <span>{r.motivo}</span>}
              </p>
              {!daVersaoEscolhida && (
                <p className="editor-do-mapa__painel-ajuda">
                  {`Este resultado é da versão ${ultima.numero}. Simule de novo para ver a versão ${fonteDe(versao).versao}.`}
                </p>
              )}

              <h3 className="editor-do-mapa__paleta-titulo">O caminho</h3>
              {r.passos.length === 0 ? (
                <p className="editor-do-mapa__painel-ajuda">Não passaria por nenhum bloco.</p>
              ) : (
                <ol className="editor-do-mapa__caminho">
                  {r.passos.map((p, i) => {
                    const titulo = tituloDoPasso(p.no, p.tipo);
                    const tipo = tipoDoPasso(p.tipo, blocoDoPasso(p.no)?.tipo, acoesDoCatalogo);
                    return (
                      <li
                        key={`${p.no ?? p.tipo}-${i}`}
                        className="editor-do-mapa__caminho-passo"
                        data-resultado={p.resultado}
                        style={{ '--i': i } as CSSProperties}
                      >
                        <span
                          className="editor-do-mapa__caminho-icone"
                          data-grupo={grupoDoTipo(tipo)}
                          aria-hidden="true"
                        >
                          <Icone nome={p.resultado === 'falhou' ? 'alerta' : iconeDoTipo(tipo)} />
                        </span>
                        <span className="editor-do-mapa__caminho-textos">
                          <strong>{titulo}</strong>
                          {p.saida && (
                            <span
                              className="editor-do-mapa__caminho-saida"
                              style={{ '--tom': COR_DO_TOM[tomDaSaida(p.saida)] } as CSSProperties}
                            >
                              saiu por {rotuloDaSaida(p.saida) ?? p.saida}
                            </span>
                          )}
                          {p.motivo && (
                            <span className="editor-do-mapa__passo-motivo">{p.motivo}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}

              {aoVerNoMapa && daVersaoDoMapa && simulada.grafo && r.passos.length > 0 && (
                <Botao variante="secundario" onClick={() => aoVerNoMapa(ultima)}>
                  <Icone nome="olho" />
                  Ver o caminho no mapa
                </Botao>
              )}

              {r.acoes.length > 0 && (
                <>
                  <h3 className="editor-do-mapa__paleta-titulo">Pediria ao CRM</h3>
                  <ul className="editor-do-mapa__caminho">
                    {r.acoes.map((x, i) => (
                      <li
                        key={`${x.acao}-${i}`}
                        className="editor-do-mapa__caminho-passo"
                        style={{ '--i': r.passos.length + i } as CSSProperties}
                      >
                        <span
                          className="editor-do-mapa__caminho-icone"
                          data-grupo="acao"
                          aria-hidden="true"
                        >
                          <Icone nome="enviar" />
                        </span>
                        <span className="editor-do-mapa__caminho-textos">
                          <strong>{tituloDaAcao(x.acao)}</strong>
                          {x.parametros.length > 0 && (
                            <span className="editor-do-mapa__passo-motivo">
                              com {x.parametros.join(', ')}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {r.agendamentos.length > 0 && (
                <p className="editor-do-mapa__simular-nota">
                  <Icone nome="calendario" />
                  <span>
                    Agendaria para{' '}
                    {r.agendamentos.map((x) => QUANDO.format(new Date(x.em))).join(', ')}.
                  </span>
                </p>
              )}
              {r.espera_em && (
                <p className="editor-do-mapa__simular-nota">
                  <Icone nome="relogio" />
                  {/* `espera_em` é o bloco onde pararia, não uma data. */}
                  <span>Pararia esperando em “{tituloDoPasso(r.espera_em, '')}”.</span>
                </p>
              )}
              {Object.keys(r.contagens).length > 0 && (
                <p className="editor-do-mapa__simular-nota">
                  <Icone nome="documento" />
                  <span>
                    Leria{' '}
                    {Object.values(r.contagens)
                      .map((n) => (n === 1 ? '1 linha' : `${n} linhas`))
                      .join(', ')}{' '}
                    do banco da empresa.
                  </span>
                </p>
              )}
            </section>
          )}
        </div>
      </form>
    </Dialogo>
  );
}
